import { forwardRef, type ButtonHTMLAttributes } from 'react'
import { cva, type VariantProps } from 'class-variance-authority'
import { Slot } from '@radix-ui/react-slot'
import { cn } from '@/lib/utils'

const buttonVariants = cva(
  'inline-flex items-center justify-center gap-2 whitespace-nowrap font-medium no-underline transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-accent-600 focus-visible:ring-offset-2 disabled:pointer-events-none disabled:opacity-50',
  {
    variants: {
      variant: {
        primary: 'bg-accent-600 text-white hover:bg-accent-700 active:bg-accent-800',
        secondary: 'bg-primary-900 text-white hover:bg-primary-800 active:bg-primary-700',
        outline: 'border-2 border-primary-900 text-primary-900 hover:bg-primary-900 hover:text-white',
        ghost: 'text-primary-900 hover:bg-neutral-100 active:bg-neutral-200',
      },
      size: {
        sm: 'h-9 rounded-md px-4 text-sm',
        md: 'h-11 rounded-md px-6 text-base',
        lg: 'h-13 rounded-lg px-8 text-lg',
      },
    },
    defaultVariants: {
      variant: 'primary',
      size: 'md',
    },
  }
)

interface ButtonProps
  extends ButtonHTMLAttributes<HTMLButtonElement>,
    VariantProps<typeof buttonVariants> {
  asChild?: boolean
}

const Button = forwardRef<HTMLButtonElement, ButtonProps>(
  ({ className, variant, size, asChild = false, ...props }, ref) => {
    const Comp = asChild ? Slot : 'button'
    return (
      <Comp
        className={cn(buttonVariants({ variant, size, className }))}
        ref={ref}
        {...props}
      />
    )
  }
)
Button.displayName = 'Button'

export { Button, buttonVariants }
